import type { Opaque } from 'packages/lib/src/opaque';

import type { CadenzaUrl } from '@disy/lib.backend/cadenza-url';

import type { ObjectPrivilege } from 'cadenza/access-management/model/object-privilege.gen';
import type { CadenzaLinkTargetSettings } from 'cadenza/repository/model/cadenza-link-target-settings';
import type { ExternalLinkExtensionId } from 'cadenza/repository/model/external-link-extension-id.gen';
import type { ItemType } from 'cadenza/repository/model/item-type.gen';
import type { ProjectId } from 'cadenza/repository/model/project-id.gen';
import type { ProjectKind } from 'cadenza/repository/model/project-kind.gen';
import type { RepositoryItemId } from 'cadenza/repository/model/repository-item-id';
import type { RepositoryName } from 'cadenza/repository/model/repository-name.gen';
import type { WorkbookId } from 'cadenza/repository/model/workbook-id.gen';
import type { WorkbookName } from 'cadenza/repository/model/workbook-name.gen';
import type { ReportId } from 'cadenza/workbook-report/dto/report-id';
import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import type { ViewType } from 'cadenza/workbook/api/view-type';
import type { WorksheetId } from 'cadenza/workbook/api/worksheet-id';
import type { WorksheetIconType } from 'cadenza/workbook/model/worksheet-icon-type';

/** these definitions are based on `NavigatorTreeJsonSerializer` */

export type NavigatorTreeNodeDto<T extends ItemType = ItemType> =
  T extends 'FOLDER' ? FolderNavigatorTreeNodeDto
  : T extends 'PROJECT' ? ProjectNavigatorTreeNodeDto
  : T extends 'REPORT' ? ReportNavigatorTreeNodeDto
  : T extends 'WORKBOOK' ? WorkbookNavigatorTreeNodeDto
  : T extends 'WORKSHEET' ? WorksheetNavigatorTreeNodeDto
  : T extends 'WORKBOOK_VIEW' ? WorkbookViewNavigatorTreeNodeDto
  : T extends 'EXTERNAL_LINK_EXTENSION' ? ExternalLinkExtensionNavigatorTreeNodeDto
  : never;

export type NavigatorTreeNodeId = Opaque<string, 'NavigatorTreeNodeId'>;

interface BaseNavigatorTreeNodeDto<T extends ItemType> {
  /** A tree-wide unique id generated on the client side, may differ after tree re-retrieval. */
  id: NavigatorTreeNodeId;
  itemId?: RepositoryItemId;
  itemType: T; // not all nodes have `itemId`s, but whenever they do `itemType === itemId.itemType`
  text: string; // printName
  labels?: string[];
  action?: NavigatorTreeNodeAction;
  /** Only present on the filtered tree in NavigatorTreeService. */
  filterMatch?: {
    terms: string[];
    matchingLabels?: Record<string, string[]>;
  };
  parent: NavigatorTreeNodeDto & ParentNavigatorTreeNodeDto; // created on the frontend
}

export type FolderNavigatorTreeNodeDto = BaseNavigatorTreeNodeDto<'FOLDER'> & ParentNavigatorTreeNodeDto;

interface ItemNavigatorTreeNodeDto<T extends ItemType> extends BaseNavigatorTreeNodeDto<T> {
  action: NavigatorTreeNodeAction;
  privileges: ObjectPrivilege[];
}

interface NavigatorTreeNodeAction {
  url: CadenzaUrl;
}

interface ProjectNavigatorTreeNodeDto
  extends BaseNavigatorTreeNodeDto<'PROJECT'>, ParentNavigatorTreeNodeDto<WorkbookNavigatorTreeNodeDto> {
  itemId: ProjectId;
  projectKind: ProjectKind;
  action: NavigatorTreeNodeAction;
}

export function isProjectNode(node: NavigatorTreeNodeDto): node is ProjectNavigatorTreeNodeDto {
  return node.itemType === 'PROJECT';
}

export interface ReportNavigatorTreeNodeDto extends BaseNavigatorTreeNodeDto<'REPORT'> {
  reportId: ReportId;
  parent: WorkbookNavigatorTreeNodeDto; // created on the frontend
}

export interface WorkbookNavigatorTreeNodeDto
  extends BaseNavigatorTreeNodeDto<'WORKBOOK'>, ParentNavigatorTreeNodeDto<WorksheetNavigatorTreeNodeDto> {
  itemId: WorkbookId;
  workbookName: WorkbookName;
  repositoryName: RepositoryName;
  repositoryPrintName: string;
  action: NavigatorTreeNodeAction;
  privileges: ObjectPrivilege[];
  userIsOwnerOfWorkbook: boolean;
  userHasDirectShareOnWorkbook: boolean;
  parent: FolderNavigatorTreeNodeDto | ProjectNavigatorTreeNodeDto;
}

export function isWorkbookNode(node: NavigatorTreeNodeDto): node is WorkbookNavigatorTreeNodeDto {
  return node.itemType === 'WORKBOOK';
}

export interface WorksheetNavigatorTreeNodeDto
  extends ItemNavigatorTreeNodeDto<'WORKSHEET'>, ParentNavigatorTreeNodeDto<WorkbookViewNavigatorTreeNodeDto> {
  workbookName: WorkbookName;
  worksheetId: WorksheetId;
  worksheetVisible: boolean;
  worksheetIcon: WorksheetIconType;
  parent: WorkbookNavigatorTreeNodeDto; // created on the frontend
}

export function isWorksheetNode(node: NavigatorTreeNodeDto): node is WorksheetNavigatorTreeNodeDto {
  return node.itemType === 'WORKSHEET';
}

export interface WorkbookViewNavigatorTreeNodeDto extends ItemNavigatorTreeNodeDto<'WORKBOOK_VIEW'> {
  workbookName: WorkbookName;
  worksheetId: WorksheetId;
  viewId: ViewId;
  viewType: ViewType;
  visualizationKeys: DataViewVisualizationKey[];
  parent: WorksheetNavigatorTreeNodeDto; // created on the frontend
  overviewMap: boolean;
}

export function isViewNode(node: NavigatorTreeNodeDto): node is WorkbookViewNavigatorTreeNodeDto {
  return node.itemType === 'WORKBOOK_VIEW';
}

interface ExternalLinkExtensionNavigatorTreeNodeDto extends ItemNavigatorTreeNodeDto<'EXTERNAL_LINK_EXTENSION'> {
  itemId: ExternalLinkExtensionId;
  action: ExternalLinkExtensionNavigatorTreeNodeAction;
}

interface ExternalLinkExtensionNavigatorTreeNodeAction extends NavigatorTreeNodeAction {
  printName: string;
  targetSettings: CadenzaLinkTargetSettings;
}

export function isExternalLinkExtensionNode(
  node: NavigatorTreeNodeDto,
): node is ExternalLinkExtensionNavigatorTreeNodeDto {
  return node.itemType === 'EXTERNAL_LINK_EXTENSION';
}

export interface ParentNavigatorTreeNodeDto<CHILD extends NavigatorTreeNodeDto = NavigatorTreeNodeDto> {
  children: CHILD[];
}

export function isParentNode(node: NavigatorTreeNodeDto): node is NavigatorTreeNodeDto & ParentNavigatorTreeNodeDto {
  return 'children' in node && node.children != null;
}

export function isFolderNode(node: NavigatorTreeNodeDto): node is FolderNavigatorTreeNodeDto {
  return node.itemType === 'FOLDER';
}
